<template>
  <div class="additional-image-edit">
    <!-- 로딩 중일 때 표시 -->
    <div v-if="loading" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p>{{ progressMessage || '추가 이미지 작업 중...' }}</p>
    </div>

    <!-- 미리보기 모드 UI -->
    <div v-if="isPreviewMode" class="preview-overlay">
      <div class="preview-scroll-container">
        <div v-if="processedImages.filter(img => img.textRemoved === true).length > 0" class="images-list">
          <div v-for="(image, index) in processedImages.filter(img => img.textRemoved === true)" :key="`preview-${index}`" class="preview-image-item">
            <div class="image-number">{{ index + 1 }}</div>
            <img :src="image.url" :alt="`추가 이미지 ${index + 1}`" class="preview-image">
          </div>
        </div>
        <div v-else class="no-images-placeholder">
          <i class="las la-images"></i>
          <p>선택된 추가 이미지가 없습니다</p>
        </div>
      </div>
      <!-- 닫기 버튼 -->
      <button class="close-preview-btn" @click="isPreviewMode = false">
        <i class="las la-times"></i>
      </button>
    </div>

    <!-- 이미지 편집 화면 -->
    <div v-else-if="!isPreviewMode" class="image-editor-layout">
      <!-- 좌측: 이미지 선택 패널 -->
      <div class="image-selector-panel">
        <div class="panel-header">
          <h5 class="panel-title">이미지 대체</h5>
        </div>

        <!-- 이미지 탭 (통합) -->
        <div class="image-tabs">
          <b-nav tabs>
            <b-nav-item :active="true">
              <span class="tab-title">모든 이미지</span>
              <b-badge v-if="allImages.length > 0" variant="primary" pill>{{ allImages.length }}</b-badge>
            </b-nav-item>
            <b-button variant="outline-primary" size="sm" class="ml-auto mr-2 mt-1" @click="showImageUpload" title="이미지 업로드">
              <i class="las la-upload"></i> 업로드
            </b-button>
          </b-nav>
        </div>

        <!-- 이미지 목록 -->
        <div class="image-gallery">
          <!-- 모든 이미지 (추가 이미지와 메인 이미지 통합) -->
          <div class="image-grid">
            <!-- 업로드 파일 목록 표시 (업로드 패널을 모든 이미지 안에 통합) -->
            <div v-if="uploadFiles && uploadFiles.length > 0" class="upload-panel-inline">
              <h6>업로드할 파일 ({{ uploadFiles.length }}개)</h6>
              <div class="file-list">
                <div v-for="(file, index) in uploadFiles" :key="`file-${index}`" class="file-item">
                  <div class="file-name">{{ file.name }}</div>
                  <div class="file-size">{{ formatFileSize(file.size) }}</div>
                </div>
              </div>
              <div class="mt-3 text-center">
                <b-button variant="primary" @click="handleUploadFiles">
                  <i class="las la-upload"></i> 업로드 시작
                </b-button>
              </div>
            </div>

            <div v-if="allImages.length > 0">
              <div>
                <!-- 메인 이미지 표시 -->
                <div v-for="(image, index) in availableMainImages" :key="`main-${index}`" class="image-item"
                  @mouseover="hoveredImage = {type: 'main', index, image}"
                  @mouseleave="hoveredImage = null"
                  @click="selectImage('main', index, image)">
                  <img :src="image" alt="메인 이미지" class="img-fluid">
                  <div class="image-label">메인 이미지</div>

                  <!-- 마우스 오버 시 + 아이콘 표시 -->
                  <div v-if="hoveredImage && hoveredImage.type === 'main' && hoveredImage.index === index"
                       class="image-add-overlay"
                       @click.stop="addToProcessedImages('main', index, image)">
                    <i class="las la-plus"></i>
                  </div>
                </div>

                <!-- 처리된 추가 이미지 표시 (드래그 가능) -->
                <draggable
                  v-model="processedImagesFiltered"
                  class="processed-images-container"
                  :options="{
                    animation: 200,
                    ghostClass: 'ghost-item',
                    chosenClass: 'chosen-item',
                    dragClass: 'drag-item'
                  }"
                  @end="onProcessedImagesDragEnd">
                  <div v-for="(image, index) in processedImagesFiltered" :key="`processed-${image.url}-${index}`" class="image-item"
                    @mouseover="hoveredImage = {type: 'processed', index, image}"
                    @mouseleave="hoveredImage = null"
                    @click="selectProcessedImageFromFiltered(index, image)">
                    <img :src="image.url" alt="추가 이미지" class="img-fluid">
                    <div class="image-number-badge">{{ index + 1 }}</div>
                    <div class="image-label">추가 이미지</div>

                    <!-- 드래그 핸들 -->
                    <div class="drag-handle-small" title="드래그하여 순서 변경">
                      <i class="las la-grip-vertical"></i>
                    </div>

                    <!-- 마우스 오버 시 + 아이콘 표시 -->
                    <div v-if="hoveredImage && hoveredImage.type === 'processed' && hoveredImage.index === index"
                         class="image-add-overlay"
                         @click.stop="addToProcessedImages('processed', index, image)">
                      <i class="las la-plus"></i>
                    </div>
                  </div>
                </draggable>
              </div>
            </div>
            <div v-else class="empty-gallery">
              <i class="las la-images"></i>
              <p>사용 가능한 이미지가 없습니다</p>
              <p class="small text-muted">이미지를 선택한 후 'AI 처리' 버튼을 눌러 이미지를 추가하세요</p>
              <b-button variant="outline-primary" size="sm" @click="showImageUpload" class="mt-3">
                <i class="las la-upload"></i> 이미지 업로드
              </b-button>
            </div>
          </div>
        </div>

        <!-- 일괄 처리 및 저장 버튼 -->
        <div class="batch-process-panel">
          <b-button variant="primary" size="sm" class="w-100 mb-2" @click="processAllImages"
            :disabled="availableMainImages.length === 0">
            <i class="las la-magic"></i> 모든 이미지 일괄 처리
          </b-button>
          <b-button variant="success" size="sm" class="w-100" @click="saveAdditionalImages"
            :disabled="processedImages.filter(img => img.textRemoved === true).length === 0">
            <i class="las la-save"></i> 저장
          </b-button>
        </div>
      </div>

      <!-- 우측: 이미지 편집기 -->
      <div class="editor-main-panel">
        <!-- 헤더 제거 - MainImageEdit와 일관성 유지 -->

        <!-- 이미지 표시 영역 -->
        <div class="editor-wrapper">
          <!-- 이미지 작업 전: 메인 이미지 뷰어 (MainImageEdit와 유사하게) -->
          <div v-if="!showEditor && !isRemovingText" class="simple-image-viewer">
            <!-- 현재 이미지 표시 -->
            <img v-if="currentImageUrl" :src="currentImageUrl" alt="추가 이미지" class="main-preview-image">

            <!-- 이미지가 없는 경우 -->
            <div v-else class="no-image-placeholder">
              <i class="las la-image"></i>
              <p>선택된 이미지가 없습니다</p>
            </div>

            <!-- 세로로 고정된 편집 및 AI 처리 버튼 (이미지가 있을 때만 표시) -->
            <div v-if="currentImageUrl" class="editor-action-buttons">
              <b-button variant="primary" size="sm" @click="openEditor" block>
                <i class="las la-edit"></i> 편집하기
              </b-button>
              <b-button variant="success" size="sm" @click="removeTextFromImage" block>
                <i class="las la-magic"></i> AI 처리
              </b-button>
            </div>

            <!-- 하단 이미지 스크롤러 (가운데 정렬) -->
            <div v-if="processedImages.length > 0" class="bottom-image-scroller">
              <h6 class="scroller-title">현재 추가 이미지</h6>
              <div class="scroller-container-wrapper">
                <draggable
                  v-model="processedImages"
                  class="scroller-container"
                  :options="{
                    animation: 200,
                    ghostClass: 'ghost-item',
                    chosenClass: 'chosen-item',
                    dragClass: 'drag-item'
                  }"
                  @end="onDragEnd">
                  <!-- 현재 이미지 (처리된 이미지들) -->
                  <div v-for="(image, index) in processedImages" :key="`processed-${image.url}-${index}`"
                       class="scroll-image-item"
                       :class="{ active: currentImageUrl === image.url }"
                       @click="selectProcessedImage(index, image)">
                    <img :src="image.url" alt="추가 이미지" class="scroll-thumbnail">
                    <div class="image-number-badge">{{ index + 1 }}</div>
                    <!-- 드래그 핸들 -->
                    <div class="drag-handle" title="드래그하여 순서 변경">
                      <i class="las la-grip-vertical"></i>
                    </div>
                    <!-- 이미지 조작 버튼들 -->
                    <div class="item-actions">
                      <b-button variant="danger" size="sm" @click.stop="removeImage(index)" title="이미지 삭제">
                        <i class="las la-trash"></i>
                      </b-button>
                    </div>
                  </div>
                </draggable>
              </div>
            </div>
          </div>

          <!-- 이미지 작업 후: iframe 편집기 -->
          <div v-if="showEditor && !isRemovingText && editorRequestId" class="image-container-full" style="width:100%; height:100%;">
            <ImageEditor
              ref="imageEditor"
              :requestId="editorRequestId"
              :imageUrl="editorImageUrl"
              imageType="additional"
              style="width:100%; height:100%;"
              @image-edited="handleEditorImageEdited"
              @error="handleEditorError"
            />
          </div>

          <!-- requestId가 없을 때 표시할 메시지 -->
          <div v-if="showEditor && !isRemovingText && !editorRequestId" class="image-container">
            <div class="editor-error-message">
              <i class="las la-exclamation-circle"></i>
              <h5>편집기를 로드할 수 없습니다</h5>
              <p>이미지 번역 요청 ID를 얻지 못했습니다.</p>
              <b-button variant="primary" size="sm" @click="retryTextRemoval" class="mt-3">
                <i class="las la-sync"></i> 다시 시도
              </b-button>
            </div>
          </div>

          <!-- 로딩 오버레이 -->
          <div v-if="isRemovingText" class="loading-overlay">
            <div class="loading-content">
              <b-spinner variant="primary" label="글자 삭제 중..."></b-spinner>
              <p>AI가 이미지에서 글자를 제거 중입니다...</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 이미지 업로드를 위한 숨겨진 input 추가 -->
    <input ref="imageUploadInput" type="file" accept="image/*" multiple class="d-none" @change="handleImageUpload">
  </div>
</template>

<script>
import ImageEditor from './ImageEditor.vue'
import { mapState, mapActions } from 'vuex'
import imageService from '../../services/imageService'
import additionalImageService from '../../services/works/additionalImageService'
import draggable from 'vuedraggable'

export default {
  name: "AdditionalImageEdit",
  components: {
    ImageEditor,
    draggable
  },
  props: {
    wizardData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedImageType: '',
      selectedImageIndex: -1,
      selectedImageUrl: null,
      currentAdditionalImage: null,
      resizeWidth: 800,
      resizeHeight: 800,
      processedImages: [],
      uploadFiles: null,
      isRemovingText: false,
      editedImageUrl: null,
      imageHistory: {
        original: null,
        processed: null
      },
      isPreviewMode: false, // 미리보기 모드 상태 관리
      carouselInterval: 5000, // 캐러셀 자동 전환 간격 (밀리초)
      editorRequestId: '', // xiangji 에디터에 전달할 요청 ID
      editorImageUrl: '', // xiangji 에디터에 전달할 이미지 URL
      showEditor: false, // 에디터 표시 여부 (작업 완료 시에만 true)
      hoveredImage: null // 마우스 오버된 이미지 정보
    };
  },
  computed: {
    /* eslint-disable vue/no-use-v-if-with-v-for, no-unused-vars */
    // Vuex 상태 매핑
    ...mapState({
      product: state => state.selectedRocketProduct,
      processing: state => state.processing,
      progressMessage: state => state.progressMessage
    }),

    // 로딩 상태
    loading() {
      return this.processing;
    },

    // 사용 가능한 메인 이미지 (대표 이미지 제외)
    availableMainImages() {
      if (!this.product || !this.product.mainImages) return [];

      // 첫 번째 옵션의 대표 이미지 가져오기
      let usedMainImage = null;
      if (this.product.options && this.product.options.length > 0 &&
        this.product.options[0].mainImageStatus &&
        this.product.options[0].mainImageStatus.image) {
        usedMainImage = this.product.options[0].mainImageStatus.image;
      }

      // 대표 이미지로 사용된 이미지 제외
      return this.product.mainImages.filter(img => img !== usedMainImage);
    },

    // 모든 이미지 (처리된 추가 이미지 + 메인 이미지)
    allImages() {
      // 텍스트가 제거된(처리된) 추가 이미지만 필터링
      const processed = (this.processedImages || []).filter(img => img.textRemoved === true);
      const main = this.availableMainImages || [];
      return [...processed, ...main];
    },

    // 현재 작업 중인 이미지 URL
    currentImageUrl() {
      return this.currentAdditionalImage || this.selectedImageUrl || '';
    },

    // 현재 선택된 이미지를 제외한 필터링된 메인 이미지 목록
    filteredMainImages() {
      if (!this.availableMainImages) return [];

      return this.availableMainImages
        .map((url, index) => ({ url, index }))
        .filter(item => item.url !== this.currentImageUrl);
    },

    // 텍스트가 제거된 처리된 이미지들 (드래그용)
    processedImagesFiltered: {
      get() {
        return this.processedImages.filter(img => img.textRemoved === true);
      },
      set(value) {
        // 드래그로 순서가 변경된 경우 전체 processedImages 배열 업데이트
        const nonProcessedImages = this.processedImages.filter(img => img.textRemoved !== true);
        this.processedImages = [...value, ...nonProcessedImages];
      }
    }
  },
  mounted() {
    console.log('AdditionalImageEdit 마운트됨, 선택된 제품:', this.product);

    // 이미 처리된 이미지가 있으면 로드
    if (this.product && this.product.additionalImages && this.product.additionalImages.length > 0) {
      this.processedImages = [...this.product.additionalImages];

      // 첫 번째 이미지 자동 선택
      if (this.processedImages.length > 0) {
        this.selectProcessedImage(0, this.processedImages[0]);
      }
    }
    // 처리된 이미지가 없으면 기본 메인 이미지를 추가 이미지로 설정
    else if (this.product && this.product.mainImages && this.product.mainImages.length > 0) {
      // 첫 번째 옵션의 대표 이미지 가져오기
      let usedMainImage = null;
      if (this.product.options && this.product.options.length > 0 &&
          this.product.options[0].mainImageStatus &&
          this.product.options[0].mainImageStatus.image) {
        usedMainImage = this.product.options[0].mainImageStatus.image;
      }

      // 대표 이미지를 제외한 메인 이미지를 추가 이미지로 초기화
      const availableImages = this.product.mainImages.filter(img => img !== usedMainImage);

      // 각 이미지를 추가 이미지로 변환
      this.processedImages = availableImages.map((url, index) => ({
        originalUrl: url,
        url: url,
        dimensions: '', // 아직 안 잼 — 빈 칸(지어낸 기본 크기 0)
        filesize: '알 수 없음',
        textRemoved: true,  // 메인 이미지를 추가 이미지로 사용할 수 있도록 true로 설정
        requestId: ''
      }));

      // 첫 번째 이미지 선택
      if (this.processedImages.length > 0) {
        this.selectProcessedImage(0, this.processedImages[0]);
      }
    }

    // 모든 이미지 표시

    // 키보드 이벤트 리스너 추가
    window.addEventListener('keydown', this.handleKeyDown);

    // 마운트 시 기본적으로 에디터 모드 꺼두기 (이미지 먼저 보여주기)
    this.showEditor = false;

    // 0.5초 후 첫 번째 이미지 다시 선택 (비동기 데이터 로딩 고려)
    setTimeout(() => {
      if (this.processedImages.length > 0 && !this.currentImageUrl) {
        console.log('타이머: 첫 번째 이미지 선택');
        this.selectProcessedImage(0, this.processedImages[0]);
      }
    }, 500);
  },
  beforeDestroy() {
    // 컴포넌트 제거 시 이벤트 리스너 제거
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    // Vuex 액션 매핑
    ...mapActions([
      'updateRocketProduct',
      'setProcessing'
    ]),

    // 이미지 선택 처리
    async selectImage(type, index, imageUrl) {
      console.log(`이미지 선택: ${type} 이미지, 인덱스: ${index}`);

      if (!imageUrl) return;

      // 선택 상태 업데이트
      this.selectedImageType = type;
      this.selectedImageIndex = index;
      this.selectedImageUrl = imageUrl;
      this.currentAdditionalImage = imageUrl;
      this.imageHistory.original = imageUrl;

      // 항상 이미지 뷰어 먼저 표시
      this.showEditor = false;

      // 이미지 가져오거나 requestId 얻기 (동일 URL이 있는지 확인)
      try {
        // 해당 URL에 대한 처리 기록이 있는지 확인
        const existingImage = this.processedImages.find(img => img.originalUrl === imageUrl);
        if (existingImage && existingImage.requestId) {
          // 기존 처리 기록이 있으면 사용
          this.editorRequestId = existingImage.requestId;
          this.editorImageUrl = existingImage.url || imageUrl;
        } else {
          // 없으면 빈 값 설정 (텍스트 제거 시 requestId 획득 가능)
          this.editorRequestId = '';
          this.editorImageUrl = imageUrl;
        }
      } catch (error) {
        console.error('이미지 처리 요청 오류:', error);
        // 에러 발생 시 이미지 URL만 설정
        this.editorRequestId = '';
        this.editorImageUrl = imageUrl;
      }
    },

    // 처리된 이미지 선택
    selectProcessedImage(index, image) {
      console.log("처리된 이미지 선택:", index, image);

      // 이미지가 유효한지 확인
      if (!image || !image.url) {
        console.error("유효하지 않은 이미지 정보입니다.");
        return;
      }

      // processedImages 배열에서 실제 인덱스 찾기
      // 필터링된 인덱스를 전체 배열의 인덱스로 변환
      const actualIndex = this.processedImages.findIndex(img =>
        img.url === image.url && img.originalUrl === image.originalUrl
      );

      this.selectedImageType = 'processed';
      this.selectedImageIndex = actualIndex >= 0 ? actualIndex : index;
      this.selectedImageUrl = image.url;
      this.currentAdditionalImage = image.url;
      this.imageHistory.original = image.originalUrl || image.url;

      // requestId가 있으면 설정, 없으면 이미지 URL만 설정
      this.editorRequestId = image.requestId || '';
      this.editorImageUrl = image.url;

      // 처리된 이미지도 먼저 이미지 뷰어로 표시 (편집 버튼 클릭 시 에디터 표시)
      this.showEditor = false;
    },

    // 텍스트 제거 처리
    async removeTextFromImage() {
      console.log("AI 처리 시작");

      // 현재 이미지가 없으면 중단
      if (!this.currentImageUrl) {
        this.$bvToast.toast('이미지를 먼저 선택해주세요', {
          title: '알림',
          variant: 'warning',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      // 로딩 시작 - 로컬 상태만 사용 (Vuex 상태 사용 안함)
      this.isRemovingText = true;

      try {
        // 이미지 텍스트 제거 API 호출 (직접 imageService 사용)
        const result = await imageService.removeTextFromImage(this.currentImageUrl);

        if (result) {
          console.log("AI 텍스트 제거 결과:", result);

          // 텍스트가 제거된 이미지 URL
          const processedImageUrl = result.processedUrl || result.RmUrl || result.url || this.currentImageUrl;

          // 현재 이미지 업데이트
          this.currentAdditionalImage = processedImageUrl;

          // requestId 저장
          if (result.requestId) {
            this.editorRequestId = result.requestId;
            console.log("얻은 requestId:", this.editorRequestId);
          }

          // 에디터 속성 업데이트
          this.editorImageUrl = processedImageUrl;

          // 중요: 편집기를 표시하지 않고 일반 뷰어만 유지
          this.showEditor = false;

          // 처리된 이미지 목록에 추가
          const existingIndex = this.processedImages.findIndex(img => img.originalUrl === this.imageHistory.original);

          if (existingIndex >= 0) {
            // 기존 항목 업데이트
            this.processedImages[existingIndex] = {
              originalUrl: this.imageHistory.original,
              url: processedImageUrl,
              dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
              filesize: '알 수 없음', // 호스팅된 URL이므로 크기 미지정
              textRemoved: true,
              requestId: this.editorRequestId
            };
          } else {
            // 새 항목 추가
            this.processedImages.push({
              originalUrl: this.imageHistory.original,
              url: processedImageUrl,
              dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
              filesize: '알 수 없음', // 호스팅된 URL이므로 크기 미지정
              textRemoved: true,
              requestId: this.editorRequestId
            });
          }

          // additionalImageService를 통해 coupangDoc 업데이트
          await this.updateAdditionalImagesInCoupangDoc();

          // 처리 결과 메시지
          this.$bvToast.toast('이미지에서 텍스트가 제거되고 저장되었습니다.', {
            title: '처리 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        } else {
          throw new Error('이미지 처리 결과가 없습니다.');
        }
      } catch (error) {
        console.error('AI 처리 오류:', error);
        this.$bvToast.toast(`AI 처리 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 종료
        this.isRemovingText = false;
      }
    },

    // 하단 스크롤러 드래그 앤 드롭 완료 시 호출
    async onDragEnd(event) {
      console.log('하단 스크롤러 드래그 완료:', {
        oldIndex: event.oldIndex,
        newIndex: event.newIndex,
        totalImages: this.processedImages.length
      });

      // 순서가 변경된 경우에만 coupangDoc 업데이트
      if (event.oldIndex !== event.newIndex) {
        // 현재 선택된 이미지 인덱스 업데이트
        if (this.selectedImageType === 'processed') {
          if (this.selectedImageIndex === event.oldIndex) {
            // 드래그된 이미지가 현재 선택된 이미지인 경우
            this.selectedImageIndex = event.newIndex;
          } else if (this.selectedImageIndex > event.oldIndex && this.selectedImageIndex <= event.newIndex) {
            // 선택된 이미지가 드래그 범위 내에서 앞으로 밀린 경우
            this.selectedImageIndex--;
          } else if (this.selectedImageIndex < event.oldIndex && this.selectedImageIndex >= event.newIndex) {
            // 선택된 이미지가 드래그 범위 내에서 뒤로 밀린 경우
            this.selectedImageIndex++;
          }
        }

        // additionalImageService를 통해 coupangDoc 업데이트
        await this.updateAdditionalImagesInCoupangDoc();

        // 성공 메시지
        this.$bvToast.toast('이미지 순서가 변경되었습니다.', {
          title: '순서 변경 완료',
          variant: 'success',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 좌측 패널 처리된 이미지 드래그 앤 드롭 완료 시 호출
    async onProcessedImagesDragEnd(event) {
      console.log('좌측 패널 드래그 완료:', {
        oldIndex: event.oldIndex,
        newIndex: event.newIndex
      });

      // 순서가 변경된 경우에만 coupangDoc 업데이트
      if (event.oldIndex !== event.newIndex) {
        // additionalImageService를 통해 coupangDoc 업데이트
        await this.updateAdditionalImagesInCoupangDoc();

        // 성공 메시지
        this.$bvToast.toast('이미지 순서가 변경되었습니다.', {
          title: '순서 변경 완료',
          variant: 'success',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 좌측 패널에서 필터된 이미지 선택
    selectProcessedImageFromFiltered(filteredIndex, image) {
      // 전체 processedImages 배열에서의 실제 인덱스 찾기
      const actualIndex = this.processedImages.findIndex(img =>
        img.url === image.url && img.originalUrl === image.originalUrl
      );

      if (actualIndex >= 0) {
        this.selectProcessedImage(actualIndex, image);
      }
    },

    // 이미지 위로 이동 (기존 버튼용 - 필요시 유지)
    async moveImageUp(index) {
      if (index <= 0 || index >= this.processedImages.length) return;

      // 배열에서 순서 변경
      const temp = this.processedImages[index];
      this.processedImages[index] = this.processedImages[index - 1];
      this.processedImages[index - 1] = temp;

      // 현재 선택된 이미지 인덱스 업데이트
      if (this.selectedImageType === 'processed' && this.selectedImageIndex === index) {
        this.selectedImageIndex = index - 1;
      } else if (this.selectedImageType === 'processed' && this.selectedImageIndex === index - 1) {
        this.selectedImageIndex = index;
      }

      // additionalImageService를 통해 coupangDoc 업데이트
      await this.updateAdditionalImagesInCoupangDoc();
    },

    // 이미지 아래로 이동 (기존 버튼용 - 필요시 유지)
    async moveImageDown(index) {
      if (index < 0 || index >= this.processedImages.length - 1) return;

      // 배열에서 순서 변경
      const temp = this.processedImages[index];
      this.processedImages[index] = this.processedImages[index + 1];
      this.processedImages[index + 1] = temp;

      // 현재 선택된 이미지 인덱스 업데이트
      if (this.selectedImageType === 'processed' && this.selectedImageIndex === index) {
        this.selectedImageIndex = index + 1;
      } else if (this.selectedImageType === 'processed' && this.selectedImageIndex === index + 1) {
        this.selectedImageIndex = index;
      }

      // additionalImageService를 통해 coupangDoc 업데이트
      await this.updateAdditionalImagesInCoupangDoc();
    },

    // 이미지 삭제
    async removeImage(index) {
      if (index < 0 || index >= this.processedImages.length) return;

      // 이미지 삭제 확인
      // 브라우저 기본 confirm 은 탭 JS 를 멈춰 외부 자동화(CDP)가 시간 초과된다 → 화면 안 모달 사용(확인 버튼에 포커스라 Enter 로 바로 확인)
      const confirmed = await this.$bvModal.msgBoxConfirm('이 이미지를 정말 삭제하시겠습니까?', {
        title: '확인',
        okTitle: '삭제',
        okVariant: 'danger',
        cancelTitle: '취소',
        autoFocusButton: 'ok',
        centered: true
      });
      if (confirmed) {
        // 현재 선택된 이미지와 동일한 경우 선택 취소
        if (this.selectedImageType === 'processed' && this.selectedImageIndex === index) {
          this.selectedImageType = '';
          this.selectedImageIndex = -1;
          this.selectedImageUrl = null;
          this.currentAdditionalImage = null;
        }

        // 배열에서 제거
        this.processedImages.splice(index, 1);

        // additionalImageService를 통해 coupangDoc 업데이트
        await this.updateAdditionalImagesInCoupangDoc();
      }
    },

    // 모든 이미지 일괄 처리
    async processAllImages() {
      if (this.availableMainImages.length === 0) {
        this.$bvToast.toast('처리할 메인 이미지가 없습니다.', {
          title: '알림',
          variant: 'warning',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      // 로딩 시작 - 일괄 처리는 전체 화면 로딩이므로 isRemovingText 대신 Vuex 상태 사용
      // 이 경우는 전체 모달을 차단하는 로딩이므로 중복 문제가 없음
      this.setProcessing({
        isProcessing: true,
        message: '모든 이미지 처리 중...'
      });

      try {
        // 이미지 배열 준비
        const processedImages = [];

        // 각 이미지를 순차적으로 처리
        for (const imageUrl of this.availableMainImages) {
          try {
            // 이미지 텍스트 제거 API 호출
            const result = await imageService.removeTextFromImage(imageUrl);

            if (result) {
              // 텍스트가 제거된 이미지 URL
              const processedImageUrl = result.RmUrl || result.url || imageUrl;

              // 처리된 이미지 목록에 추가
              processedImages.push({
                originalUrl: imageUrl,
                url: processedImageUrl,
                dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
                filesize: '알 수 없음', // 호스팅된 URL이므로 크기 미지정
                textRemoved: !!result.RmUrl,
                requestId: result.requestId || ''
              });
            }
          } catch (error) {
            console.error(`이미지 처리 오류: ${imageUrl}`, error);
          }
        }

        // 처리된 이미지 저장
        if (processedImages.length > 0) {
          this.processedImages = [...this.processedImages, ...processedImages];

          // 처리 완료

          // 성공 메시지
          this.$bvToast.toast(`${processedImages.length}개 이미지가 성공적으로 처리되었습니다.`, {
            title: '처리 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        } else {
          throw new Error('처리된 이미지가 없습니다.');
        }
      } catch (error) {
        console.error('일괄 처리 오류:', error);
        this.$bvToast.toast(`이미지 처리 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 종료
        this.setProcessing({ isProcessing: false });
      }
    },

    // coupangDoc에 추가 이미지 업데이트 (additionalImageService 사용)
    async updateAdditionalImagesInCoupangDoc() {
      try {
        // 텍스트가 제거된 이미지만 필터링
        const imagesToUpdate = this.processedImages.filter(img => img.textRemoved === true);

        // additionalImageService를 통해 coupangDoc 업데이트
        const updateSuccess = await additionalImageService.updateProductWithCoupangDocPartial(
          this.product,
          imagesToUpdate
        );

        if (!updateSuccess) {
          console.warn('coupangDoc 추가 이미지 업데이트 실패');
        }

        return updateSuccess;
      } catch (error) {
        console.error('coupangDoc 추가 이미지 업데이트 오류:', error);
        return false;
      }
    },

    // 추가 이미지 저장
    async saveAdditionalImages() {
      console.log("추가 이미지 저장 시작");

      // 텍스트가 제거된 이미지만 필터링
      const imagesToSave = this.processedImages.filter(img => img.textRemoved === true);

      if (imagesToSave.length === 0) {
        this.$bvToast.toast('저장할 추가 이미지가 없습니다.', {
          title: '알림',
          variant: 'warning',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      try {
        // 로딩 상태 시작
        this.setProcessing({
          isProcessing: true,
          message: '추가 이미지 저장 중...'
        });

        // additionalImageService를 통해 coupangDoc 업데이트
        const updateSuccess = await this.updateAdditionalImagesInCoupangDoc();

        if (updateSuccess) {
          this.$bvToast.toast(`${imagesToSave.length}개의 추가 이미지가 저장되었습니다.`, {
            title: '저장 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        } else {
          this.$bvToast.toast('추가 이미지 저장 중 일부 오류가 발생했습니다.', {
            title: '경고',
            variant: 'warning',
            autoHideDelay: 4000,
            toaster: 'b-toaster-bottom-right'
          });
        }

        console.log("추가 이미지 저장 완료:", imagesToSave);
      } catch (error) {
        console.error('추가 이미지 저장 오류:', error);
        this.$bvToast.toast('추가 이미지 저장 중 오류가 발생했습니다.', {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 상태 종료
        this.setProcessing({ isProcessing: false });
      }
    },

    // 이미지 편집기에 변경사항 저장 요청
    async applyEditorChanges() {
      try {
        // 편집기가 있는지 확인
        if (!this.$refs.imageEditor) {
          throw new Error('이미지 편집기를 찾을 수 없습니다');
        }

        // 로딩 상태 시작
        this.isRemovingText = true;

        // 외부 에디터에 저장 요청
        this.$refs.imageEditor.saveImage();

        // 참고: 실제 저장 처리는 handleEditorImageEdited 메서드에서 수행됨
      } catch (error) {
        console.error('에디터 변경사항 적용 중 오류:', error);
        this.$bvToast.toast(`이미지 저장 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
        // 로딩 상태 종료
        this.isRemovingText = false;
      }
    },

    // 외부 에디터에서 편집된 이미지 이벤트 처리
    async handleEditorImageEdited(editorResult) {
      console.log('에디터 이미지 편집 결과:', editorResult);

      if (!editorResult || !editorResult.imageUrl) {
        console.error('편집된 이미지 URL이 없습니다');
        this.$bvToast.toast('편집된 이미지를 가져오지 못했습니다', {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      try {
        // 로딩 상태 시작
        this.isRemovingText = true;

        // 이미지 URL 설정 (이미 호스팅된 URL)
        this.editedImageUrl = editorResult.imageUrl;
        this.currentAdditionalImage = editorResult.imageUrl;

        // requestId 저장 (에디터가 반환한 경우 사용, 아니면 기존 것 유지)
        if (editorResult.requestId) {
          this.editorRequestId = editorResult.requestId;
        }

        // 처리된 이미지 업데이트
        await this.updateProcessedImage(editorResult.imageUrl);

        // 저장 후에는 이미지 뷰어 모드로 전환 (에디터 닫기)
        this.showEditor = false;

        // 성공 메시지 표시
        this.$bvToast.toast('이미지가 저장되었습니다', {
          title: '완료',
          variant: 'success',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
      } catch (error) {
        console.error('편집된 이미지 처리 중 오류:', error);
        this.$bvToast.toast(`이미지 저장 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 상태 종료
        this.isRemovingText = false;
      }
    },

    // 처리된 이미지 업데이트
    async updateProcessedImage(imageUrl) {
      // 편집된 이미지에 대한 requestId 저장
      const requestId = this.editorRequestId || '';

      // 이미지 데이터 생성
      const imageData = {
        originalUrl: this.imageHistory.original || imageUrl,
        url: imageUrl,
        dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
        filesize: '알 수 없음',
        textRemoved: true,
        requestId: requestId
      };

      // 기존 이미지 찾기
      const existingIndex = this.processedImages.findIndex(img => img.originalUrl === this.imageHistory.original);

      if (existingIndex >= 0) {
        // 기존 항목 업데이트
        this.processedImages[existingIndex] = imageData;
      } else {
        // 새 항목 추가
        this.processedImages.push(imageData);
      }

      // additionalImageService를 통해 coupangDoc 업데이트
      await this.updateAdditionalImagesInCoupangDoc();

      // 탭 변경
      this.activeTab = 'processed';
    },

    // 에디터 초기화
    resetEditor() {
      if (this.$refs.imageEditor) {
        // 에디터 초기화 메서드 호출
        this.$refs.imageEditor.resetEditor();
        this.editedImageUrl = null;

        // 필요한 경우 원본 이미지로 돌아감
        const originalImage = this.imageHistory.original;
        if (originalImage && originalImage !== this.editorImageUrl) {
          this.editorImageUrl = originalImage;

          // 비동기로 이미지 텍스트 제거 요청하여 requestId 얻기
          if (originalImage.startsWith('http')) {
            imageService.removeTextFromImage(originalImage)
              .then(result => {
                if (result && result.requestId) {
                  this.editorRequestId = result.requestId;
                  // 에디터 재설정
                  this.$nextTick(() => {
                    if (this.$refs.imageEditor) {
                      this.$refs.imageEditor.resetEditor();
                    }
                  });
                }
              })
              .catch(error => {
                console.error('이미지 처리 요청 오류:', error);
              });
          }
        }
      }
    },

    // 파일 크기 포맷팅
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(1) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(1) + ' MB';
      }
    },

    // 파일 업로드 처리
    async handleImageUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      this.uploadFiles = files;
      this.activeTab = 'upload';
    },

    // 업로드 파일 처리
    async handleUploadFiles() {
      if (!this.uploadFiles || this.uploadFiles.length === 0) return;

      try {
        // 로딩 시작
        this.setProcessing({
          isProcessing: true,
          message: '파일 업로드 중...'
        });

        // 업로드 처리 (additionalImageService 유지)
        const uploadedImages = await additionalImageService.uploadFiles(
          this.uploadFiles,
          this.product.registrationId
        );

        if (uploadedImages && uploadedImages.length > 0) {
          // 처리된 이미지에 추가
          this.processedImages = [...this.processedImages, ...uploadedImages];

          // additionalImageService를 통해 coupangDoc 업데이트
          await this.updateAdditionalImagesInCoupangDoc();

          // 업로드 파일 초기화
          this.uploadFiles = null;

          // 성공 메시지
          this.$bvToast.toast(`${uploadedImages.length}개 이미지가 업로드되고 저장되었습니다.`, {
            title: '성공',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        }
      } catch (error) {
        console.error('파일 업로드 오류:', error);
        this.$bvToast.toast(`파일 업로드 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 종료
        this.setProcessing({ isProcessing: false });
      }
    },

    // 이미지 업로드 입력 표시
    showImageUpload() {
      this.$refs.imageUploadInput.click();
    },

    // 에디터 오류 처리
    handleEditorError() {
      console.error("이미지 편집기 로드 오류");
      this.$bvToast.toast('이미지 편집기 로드 중 오류가 발생했습니다', {
        title: '오류',
        variant: 'danger',
        autoHideDelay: 3000,
        toaster: 'b-toaster-bottom-right'
      });
    },

    // 키보드 이벤트 처리
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.isPreviewMode) {
        this.isPreviewMode = false;
      }
    },

    // 편집기 열기
    openEditor() {
      // 현재 이미지가 없으면 무시
      if (!this.currentImageUrl) {
        this.$bvToast.toast('이미지를 먼저 선택해주세요', {
          title: '알림',
          variant: 'warning',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      // 편집기 열기
      // requestId 확인 - 있으면 바로 에디터 열기
      if (this.editorRequestId) {
        this.showEditor = true;

        // 에디터 리셋 (다음 렌더링 사이클에서)
        this.$nextTick(() => {
          if (this.$refs.imageEditor) {
            this.$refs.imageEditor.resetEditor();
          }
        });
      } else {
        // requestId가 없으면 알림
        this.$bvToast.toast('먼저 AI 처리 버튼을 클릭하여 이미지를 처리해주세요', {
          title: '알림',
          variant: 'info',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 텍스트 제거 다시 시도
    retryTextRemoval() {
      console.log("텍스트 제거 재시도");

      // 현재 이미지로 텍스트 제거 다시 시도
      if (this.currentImageUrl) {
        this.removeTextFromImage();
      } else {
        this.$bvToast.toast('이미지를 먼저 선택해주세요', {
          title: '알림',
          variant: 'warning',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 이미지를 현재 추가 이미지에 추가
    async addToProcessedImages(type, index, image) {
      console.log(`이미지 추가: ${type} 이미지, 인덱스: ${index}`);

      try {
        // 원본 이미지 URL 가져오기 (타입에 따라 다른 방식 사용)
        const originalUrl = type === 'main' ? image : image.originalUrl || image.url;
        const imageUrl = type === 'main' ? image : image.url;

        // 이미 추가된 이미지인지 확인
        const existingIndex = this.processedImages.findIndex(img =>
          img.originalUrl === originalUrl || img.url === imageUrl
        );

        if (existingIndex >= 0) {
          this.$bvToast.toast('이미 추가된 이미지입니다', {
            title: '알림',
            variant: 'info',
            autoHideDelay: 2000,
            toaster: 'b-toaster-bottom-right'
          });
          return;
        }

        // AI 처리 없이 추가 이미지로 바로 추가
        const newImage = {
          originalUrl: originalUrl,
          url: imageUrl,
          dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
          filesize: '알 수 없음',
          textRemoved: true, // 모든 추가된 이미지는 저장될 수 있도록 true로 설정
          requestId: type === 'processed' ? (image.requestId || '') : ''
        };

        // 이미지 목록에 추가
        this.processedImages.push(newImage);

        // additionalImageService를 통해 coupangDoc 업데이트
        await this.updateAdditionalImagesInCoupangDoc();

        // 추가 메시지
        this.$bvToast.toast('이미지가 추가되고 저장되었습니다.', {
          title: '추가 완료',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });

        // 추가된 이미지로 현재 이미지 변경
        this.selectProcessedImage(this.processedImages.length - 1, newImage);

      } catch (error) {
        console.error('이미지 추가 오류:', error);
        this.$bvToast.toast(`이미지 추가 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
      }
    }
  }
};
</script>

<style scoped>
.additional-image-edit {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 로딩 컨테이너 */
.loading-container {
  text-align: center;
  padding: 40px 0;
}

/* 레이아웃 컨테이너 */
.image-editor-layout {
  display: flex;
  height: 100%;
  background-color: #f9f9f9;
}

/* 좌측 이미지 선택 패널 */
.image-selector-panel {
  width: 280px;
  background-color: #fff;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

/* 이미지 탭 스타일 */
.image-tabs {
  border-bottom: 1px solid #eee;
}

.image-tabs .nav-tabs {
  border-bottom: none;
}

.image-tabs .nav-link {
  font-size: 12px;
  padding: 5px 10px;
  color: #777;
  display: flex;
  align-items: center;
  gap: 5px;
}

.image-tabs .nav-link.active {
  color: #3498db;
  font-weight: 600;
}

.tab-title {
  margin-right: 3px;
}

.image-tabs .badge {
  font-size: 9px;
  padding: 3px 6px;
  height: 17px;
  line-height: 11px;
  font-weight: 400;
}

.image-gallery {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 20px; /* 간격 증가 */
  width: 100%;
  padding: 15px;
}

.image-item {
  position: relative;
  border: 2px solid transparent;
  border-radius: 8px; /* 더 둥글게 */
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  aspect-ratio: 1/1;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1); /* 그림자 추가 */
  margin-bottom: 5px; /* 하단 여백 추가 */
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.image-item.selected {
  border-color: #3498db;
}

.image-overlay {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-item.selected .image-overlay {
  opacity: 1;
}

/* 새로운 추가 버튼 스타일 */
.image-add-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: all 0.2s;
  cursor: pointer;
  z-index: 5;
}

.image-add-overlay i {
  font-size: 24px;
  background: rgba(255, 255, 255, 0.8);
  color: #3498db;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.image-add-overlay:hover {
  background: rgba(0, 0, 0, 0.5);
}

.image-add-overlay:hover i {
  transform: scale(1.1);
  background: #fff;
}

.image-number-badge {
  position: absolute;
  top: 5px;
  left: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 11px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  padding: 0 6px;
}

/* 이미지 라벨 */
.image-label {
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 3px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-actions {
  position: absolute;
  bottom: 5px;
  right: 5px;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-item:hover .image-actions {
  opacity: 1;
}

.empty-gallery {
  height: 100%;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #aaa;
  text-align: center;
  padding: 20px;
}

.empty-gallery i {
  font-size: 48px;
  margin-bottom: 10px;
}

.empty-gallery .small {
  font-size: 12px;
  margin-top: 5px;
}

/* 이미지 처리 옵션 */
.image-options {
  padding: 15px;
  border-top: 1px solid #eee;
}

/* 현재 메인 이미지 미리보기 스타일 */
.current-images-preview {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px;
  background-color: #f9f9f9;
}

.options-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #555;
}

.additional-images-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}

.mini-image-container {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* 1:1 비율 유지 (정사각형) */
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mini-image-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.mini-preview-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* 이미지를 잘라서 컨테이너를 채움 */
}

.image-number {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 10px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.more-images-indicator {
  margin-top: 5px;
  text-align: center;
  font-size: 12px;
  color: #777;
}

.small-placeholder {
  color: #aaa;
  font-size: 12px;
  padding: 10px 0;
}

.small-placeholder i {
  font-size: 24px;
  margin-bottom: 5px;
}

/* 업로드 패널 */
.upload-panel-inline {
  width: 100%;
  grid-column: 1 / -1;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 15px;
  border: 1px solid #eee;
}

.upload-dropzone {
  height: 100%;
  min-height: 150px;
  border: 2px dashed #eee;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #aaa;
  padding: 20px;
}

.upload-dropzone i {
  font-size: 48px;
  margin-bottom: 10px;
}

.upload-files {
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.file-list {
  max-height: 200px;
  overflow-y: auto;
}

.file-item {
  padding: 5px 0;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
}

.file-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 10px;
}

.file-size {
  color: #777;
  white-space: nowrap;
}

/* 배치 처리 패널 */
.batch-process-panel {
  padding: 15px;
  border-top: 1px solid #eee;
}

/* 우측 에디터 패널 */
.editor-main-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 600px; /* 높이 증가 */
  position: relative;
  overflow: hidden;
  width: 100%; /* 너비 명시 */
}

.editor-header {
  padding: 10px 15px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.editor-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5; /* 배경색 변경 - 연한 회색으로 변경하여 이미지 구분 */
  padding: 0;
  min-height: 600px;
  position: relative;
  flex: 1;
  overflow: hidden;
  border-top: 1px solid #eee;
}

.simple-image-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* 중앙 정렬 */
  background-color: #f5f5f5; /* 배경색 회색으로 설정 - MainImageEdit와 일관성 */
  padding: 0 0 140px 0; /* 패딩 최소화, 하단은 스크롤러만큼만 공간 확보 */
  overflow: hidden;
}

/* 스크롤 가능한 이미지 컨테이너 */
.image-scroll-container {
  width: 100%;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  background-color: #f5f5f5; /* 배경색 회색으로 설정 */
  min-height: 100%;
}

.image-scroll-item {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  background-color: #f5f5f5; /* 배경색은 회색으로 변경 */
  border-radius: 8px;
  transition: transform 0.2s;
  margin-bottom: 20px;
}

.image-scroll-item.current-active .image-container-wrapper {
  border: 2px solid #3498db;
  background-color: #f8faff;
}

.image-scroll-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.image-caption {
  margin-top: 10px;
  margin-bottom: 5px;
  font-size: 14px;
  color: #555;
  text-align: center;
  font-weight: 500;
}

.image-actions {
  margin-top: 5px;
  margin-bottom: 15px;
}

.main-preview-image {
  width: auto;
  max-width: 100%; /* 최대 너비 */
  height: 100%; /* 높이 채우기 */
  max-height: calc(100% - 140px); /* 하단 스크롤러 공간만 확보 */
  object-fit: contain;
  display: block; /* 인라인 간격 제거 */
  background-color: transparent; /* 배경색 투명 */
  box-shadow: none; /* 그림자 제거 */
  border: none; /* 경계선 제거 */
  margin: 0; /* 여백 제거 */
}

/* 하단 이미지 스크롤러 */
.bottom-image-scroller {
  width: 100%;
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-top: auto; /* 아래에 붙이기 */
  position: absolute;
  bottom: 10px; /* 하단 간격 축소 */
  left: 0;
  right: 0;
  z-index: 5;
  text-align: center;
}

.scroller-container-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.scroller-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #555;
}

.scroller-container {
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 10px;
  margin-bottom: 10px;
  max-width: 80%;
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
  justify-content: center;
}

.scroller-container::-webkit-scrollbar {
  height: 6px;
}

.scroller-container::-webkit-scrollbar-track {
  background: transparent;
}

.scroller-container::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 6px;
}

.scroll-image-item {
  flex: 0 0 auto;
  width: 100px;
  height: 100px;
  position: relative;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  background-color: white;
}

.scroll-image-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
}

/* 스크롤 이미지 아이템 조작 버튼들 */
.item-actions {
  position: absolute;
  top: 5px;
  right: 5px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.scroll-image-item:hover .item-actions {
  opacity: 1;
}

.item-actions .btn {
  padding: 2px 4px;
  font-size: 10px;
  line-height: 1;
  min-width: 20px;
  height: 20px;
}

/* 드래그 핸들 */
.drag-handle {
  position: absolute;
  top: 5px;
  left: 5px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 6;
  cursor: grab;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 12px;
}

.scroll-image-item:hover .drag-handle {
  opacity: 1;
}

.drag-handle:active {
  cursor: grabbing;
}

/* 드래그 상태 스타일 */
.ghost-item {
  opacity: 0.5;
  background: #f0f0f0;
  border: 2px dashed #ccc;
}

.chosen-item {
  transform: scale(1.05);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1000;
}

.drag-item {
  transform: rotate(5deg);
  opacity: 0.8;
}

/* 드래그 가능한 스크롤 컨테이너 */
.scroller-container {
  cursor: default;
}

.scroller-container .scroll-image-item {
  transition: transform 0.2s ease;
}

/* 좌측 패널 처리된 이미지 컨테이너 */
.processed-images-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* 좌측 패널용 작은 드래그 핸들 */
.drag-handle-small {
  position: absolute;
  top: 3px;
  left: 3px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 6;
  cursor: grab;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 2px;
  padding: 1px 3px;
  font-size: 10px;
}

.image-item:hover .drag-handle-small {
  opacity: 1;
}

.drag-handle-small:active {
  cursor: grabbing;
}

/* 이미지 아이템 드래그 상태 */
.image-item {
  transition: transform 0.2s ease;
  position: relative;
}

.image-item.ghost-item {
  opacity: 0.5;
  background: #f0f0f0;
  border: 2px dashed #ccc;
}

.image-item.chosen-item {
  transform: scale(1.05);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1000;
}

.image-item.drag-item {
  transform: rotate(3deg);
  opacity: 0.8;
}

.scroll-image-item.active {
  border-color: #3498db;
}

.scroll-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-number-badge {
  position: absolute;
  top: 5px;
  left: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 11px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  padding: 0 6px;
}

/* 이미지 라벨 */
.image-label {
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 3px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scroll-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 10px;
  padding: 3px;
  text-align: center;
}

/* 부유하는 편집 버튼 */
.editor-action-buttons {
  position: fixed; /* sticky처럼 동작하도록 fixed 사용 */
  top: 50%; /* 화면 중앙 높이에 위치 */
  right: 30px; /* 우측에 고정 */
  transform: translateY(-50%); /* 세로 중앙 정렬 */
  z-index: 20; /* 더 높은 z-index */
  opacity: 0.9;
  transition: opacity 0.2s;
  display: flex;
  flex-direction: column; /* 세로로 버튼 배치 */
  gap: 10px;
  background-color: rgba(255, 255, 255, 0.5); /* 반투명 배경 */
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.editor-action-buttons:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.8); /* 호버 시 배경 더 뚜렷하게 */
}

.floating-edit-button:hover {
  opacity: 1;
}

.image-container {
  position: relative;
  width: min(100%, 500px);
  height: min(100%, 500px);
  aspect-ratio: 1/1;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* 이미지 컨테이너 (전체 화면 버전) */
.image-container-full {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100% !important;
  height: 100% !important;
  background-color: #fff;
  box-shadow: none;
  overflow: hidden;
  z-index: 1;
  margin: 0;
  padding: 0;
  border: none;
}

/* No image placeholder */
.no-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 14px;
  padding: 40px;
  text-align: center;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.05);
  width: 300px;
}

.no-image-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

/* 에디터 오류 메시지 스타일 */
.editor-error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  text-align: center;
  color: #555;
}

.editor-error-message i {
  font-size: 48px;
  color: #dc3545;
  margin-bottom: 20px;
}

.editor-error-message h5 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.editor-error-message p {
  color: #777;
  margin-bottom: 15px;
}

/* 로딩 오버레이 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.loading-content {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.loading-content p {
  margin-top: 15px;
  color: #333;
}

/* 미리보기 모드 */
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-scroll-container {
  width: 100%;
  height: 100%;
  padding: 50px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.images-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 800px;
}

.preview-image-item {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.image-number {
  position: absolute;
  top: 15px;
  left: 15px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  z-index: 1;
}

.preview-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
}

.no-images-placeholder {
  color: white;
  text-align: center;
}

.no-images-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.close-preview-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.close-preview-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-preview-btn:focus {
  outline: none;
}
</style>